import {Alert, Snackbar} from '@mui/material';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {AuthSession} from '../App';
import {
    ChipKind,
    TabKind,
    TenantChip,
    TenantRow,
    UserRow,
    aggregateByTenant,
    aggregateUserAccess,
    emptyUsersByKind,
    tabKindOf,
} from '../lib/aggregate';
import {
    aiAccountLabel,
    isAiAccount,
    resourceAxisLabel,
    resourceColumnHeading,
} from '../lib/ai-accounts';
import {GrantsResponse, revokeGrant} from '../lib/api';
import {KIND_LABEL, KIND_ORDER, chipKindClass, kindHeading} from '../lib/chip-style';
import {GrantView} from '../lib/grant-view';

import {AccessChip, ChipVariant} from './AccessChip';
import {MatrixView} from './MatrixView';
import {MultiSelectFilter} from './MultiSelectFilter';
import {Spinner} from './Spinner';
import {TenantDrawer} from './TenantDrawer';
import {UserChip} from './UserChip';
import {UserDrawer} from './UserDrawer';

type ExpiryFilter = 'none' | '7d' | '30d';

interface GrantsPageProps {
    session: AuthSession;
    /** /grants response; undefined while the first fetch is in flight. */
    data: GrantsResponse | undefined;
    loading: boolean;
    error: string | undefined;
    twingateDown: boolean;
    onReload: () => void;
}

/**
 * Which grants the admin can actually revoke from this UI. Mirrors the
 * server-side cases: the all-tenants permanent DataAccessPolicy, any
 * TemporaryDataAccess-* policy, and any AccessOps impersonation grant.
 * Per-tenant permanent DataAccess is intentionally NOT revocable here (deleting
 * DataAccessPolicy would drop all of a user's permanent access). Anything else
 * gets a disabled Revoke button — there's no safe destination for the API call.
 */
function formatAgo(ts: number): string {
    const diff = Date.now() - ts;
    if (diff < 60_000) {
        return 'just now';
    }
    if (diff < 3_600_000) {
        return `${Math.round(diff / 60_000)}m ago`;
    }
    if (diff < 86_400_000) {
        return `${Math.round(diff / 3_600_000)}h ago`;
    }
    return `${Math.round(diff / 86_400_000)}d ago`;
}

function isRevocable(g: GrantView): boolean {
    // All-tenants permanent DataAccess (the wildcard DataAccessPolicy).
    if (g.source === 'iam-bastion' && g.kind === 'data-access' && g.resource === '*') {
        return true;
    }
    // Temporary per-shard DataAccess policies.
    if (g.source === 'iam-bastion' && g.kind === 'data-access-temporary') {
        return true;
    }
    // Any AccessOps impersonation grant (wildcard or per-shard) — a DDB row delete.
    if (g.source === 'access-ops') {
        return true;
    }
    return false;
}

interface UrlState {
    kind: TabKind;
    search: string;
    tenants: string[];
    permanent: boolean;
    expiry: ExpiryFilter;
    variant: ChipVariant | 'matrix';
    page: number;
    pageSize: number;
}

const DEFAULT_STATE: UrlState = {
    kind: 'users',
    search: '',
    tenants: [],
    permanent: false,
    expiry: 'none',
    variant: 'chips',
    page: 0,
    pageSize: 50,
};

function readUrl(): UrlState {
    const p = new URLSearchParams(window.location.search);
    const kindRaw = p.get('kind');
    let kind: TabKind;
    // 'tenants' is deliberately ABSENT: the Tenants tab is hidden (below), and a
    // hidden-but-URL-reachable view is worse than either showing it or removing
    // it — a stale bookmark would open a surface we decided not to support. The
    // aggregation code stays; only the way in is closed.
    if (
        kindRaw === 'imp-ai' ||
        kindRaw === 'imp-decisions' ||
        kindRaw === 'data-access' ||
        kindRaw === 'users'
    ) {
        kind = kindRaw;
    } else if (kindRaw === 'impersonation') {
        // Pre-split bookmarks meant Decisions — every impersonation grant that
        // existed when those links were made belonged to that app.
        kind = 'imp-decisions';
    } else if (kindRaw === 'all') {
        // Migrate the previous 'all' URL value to 'users' — it meant the combined
        // view, which is what Users is. NOT the default below: someone who
        // bookmarked 'all' asked for every capability at once.
        kind = 'users';
    } else {
        // Default landing tab. Ennabl AI is the newest capability and the one
        // being actively granted, so it leads.
        kind = 'imp-ai';
    }
    const expiry = (['7d', '30d'].includes(p.get('expiry') ?? '') ? p.get('expiry') : 'none') as ExpiryFilter;
    const variantRaw = p.get('variant') ?? 'chips';
    // 'matrix' is deliberately ABSENT for the same reason as 'tenants' above. The
    // View selector that offered it is already hidden, so the URL was the last way
    // in — and with both closed, `TenantDrawer` has no reachable entry point at
    // all (its other one was the Tenants table). MatrixView / TenantDrawer are
    // kept, unreachable, for whenever they earn their keep.
    const variant = (['chips', 'compact', 'minimal'].includes(variantRaw)
        ? variantRaw
        : 'chips') as UrlState['variant'];
    const tenants = (p.get('tenants') ?? '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
    const pageSize = Number(p.get('pageSize')) || 50;
    return {
        kind,
        search: p.get('search') ?? '',
        tenants,
        permanent: p.get('permanent') === '1',
        expiry,
        variant,
        page: Number(p.get('page')) || 0,
        pageSize: [10, 25, 50, 100].includes(pageSize) ? pageSize : 50,
    };
}

function writeUrl(s: UrlState) {
    const p = new URLSearchParams();
    if (s.kind !== DEFAULT_STATE.kind) {
        p.set('kind', s.kind);
    }
    if (s.search) {
        p.set('search', s.search);
    }
    if (s.tenants.length) {
        p.set('tenants', s.tenants.join(','));
    }
    if (s.permanent) {
        p.set('permanent', '1');
    }
    if (s.expiry !== 'none') {
        p.set('expiry', s.expiry);
    }
    if (s.variant !== 'chips') {
        p.set('variant', s.variant);
    }
    if (s.page) {
        p.set('page', String(s.page));
    }
    if (s.pageSize !== 50) {
        p.set('pageSize', String(s.pageSize));
    }
    const qs = p.toString();
    const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState(null, '', next);
}

export function GrantsPage({session, data, loading, error, twingateDown, onReload}: GrantsPageProps) {
    const [state, setState] = useState<UrlState>(() => readUrl());
    const [searchDraft, setSearchDraft] = useState(state.search);
    const [drawerEmail, setDrawerEmail] = useState<string | null>(null);
    const [tenantDrawer, setTenantDrawer] = useState<{resource: string; kind: ChipKind | 'both'} | null>(null);
    const [toast, setToast] = useState<string | null>(null);
    const searchTimer = useRef<number | null>(null);

    const update = useCallback((patch: Partial<UrlState>) => {
        setState(prev => {
            const next: UrlState = {...prev, ...patch};
            // Reset page on any filter change other than pure pagination.
            if (
                patch.search !== undefined ||
                patch.tenants !== undefined ||
                patch.permanent !== undefined ||
                patch.expiry !== undefined ||
                patch.kind !== undefined ||
                patch.pageSize !== undefined
            ) {
                next.page = 0;
            }
            // Tab switch should drop tenant selection — the previous tab's
            // tenants almost never overlap with the new one's, so carrying
            // them forward filters out everything by surprise. Caller can
            // override by setting `tenants` in the same patch.
            if (patch.kind !== undefined && patch.tenants === undefined && patch.kind !== prev.kind) {
                next.tenants = [];
            }
            writeUrl(next);
            return next;
        });
    }, []);

    useEffect(() => {
        writeUrl(state);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced search.
    useEffect(() => {
        if (searchTimer.current) {
            window.clearTimeout(searchTimer.current);
        }
        searchTimer.current = window.setTimeout(() => {
            if (searchDraft !== state.search) {
                update({search: searchDraft});
            }
        }, 200);
        return () => {
            if (searchTimer.current) {
                window.clearTimeout(searchTimer.current);
            }
        };
    }, [searchDraft, state.search, update]);

    const grants = useMemo(() => data?.grants ?? [], [data]);

    // Tab counts are unfiltered — they shouldn't shrink as the user types.
    // Users = unique emails; Tenants = unique canonical tenants.
    const tabCounts = useMemo(() => {
        const perKind: Record<ChipKind, number> = {'data-access': 0, 'imp-ai': 0, 'imp-decisions': 0};
        const userSet = new Set<string>();
        const tenantSet = new Set<string>();
        for (const g of grants) {
            const k = tabKindOf(g.kind);
            if (!k) {
                continue;
            }
            perKind[k] += 1;
            userSet.add(g.email);
            tenantSet.add(g.resourceName ?? g.resource);
        }
        return {perKind, users: userSet.size, tenants: tenantSet.size};
    }, [grants]);

    const allTenantsForKind = useMemo(() => {
        // Dedupe tenants by canonical id (name when known) so the dropdown shows one
        // option per tenant even when grants for it arrived as both a shard uid and
        // an AWS account id. Filter value = canonical; label = "<name> · <id, id, ...>".
        interface Acc {
            label: string;
            name?: string;
            ids: string[];
        }
        const byCanonical = new Map<string, Acc>();
        for (const g of grants) {
            const k = tabKindOf(g.kind);
            if (!k) {
                continue;
            }
            // Users / Tenants show every tenant; per-kind tabs filter the
            // dropdown down to that kind.
            const showsAllKinds = state.kind === 'users' || state.kind === 'tenants';
            if (!showsAllKinds && k !== state.kind) {
                continue;
            }
            const canon = g.resourceName ?? g.resource;
            let entry = byCanonical.get(canon);
            if (!entry) {
                entry = {label: canon, name: g.resourceName, ids: []};
                byCanonical.set(canon, entry);
            }
            if (!entry.name && g.resourceName) {
                entry.name = g.resourceName;
            }
            if (!entry.ids.includes(g.resource)) {
                entry.ids.push(g.resource);
            }
        }
        const options = Array.from(byCanonical.entries()).map(([value, e]) => {
            let label: string;
            if (value === '*') {
                label = 'All tenants (wildcard)';
            } else if (value === '?') {
                label = 'Unknown';
            } else if (isAiAccount(value)) {
                label = `${aiAccountLabel(value)} · ${value}`;
            } else if (e.name) {
                label = `${e.name} · ${e.ids.join(', ')}`;
            } else {
                label = value;
            }
            return {value, label, isAi: isAiAccount(value)};
        });
        // Wildcard first, then the internal Ennabl AI accounts as their own group,
        // then tenants alphabetically. AI accounts are NEVER interleaved with
        // tenants — they are a different kind of thing (an AWS account we own).
        options.sort((a, b) => {
            if (a.value === '*' || b.value === '*') {
                return a.value === '*' ? -1 : 1;
            }
            if (a.isAi !== b.isAi) {
                return a.isAi ? -1 : 1;
            }
            return a.label.localeCompare(b.label);
        });
        return options.map(({value, label}) => ({value, label}));
    }, [grants, state.kind]);

    const aggregated = useMemo<UserRow[]>(
        () => (state.kind === 'tenants' ? [] : aggregateUserAccess(grants, state.kind)),
        [grants, state.kind]
    );
    const tenantsAggregated = useMemo<TenantRow[]>(
        () => (state.kind === 'tenants' ? aggregateByTenant(grants) : []),
        [grants, state.kind]
    );
    // Per-kind tabs can suggest "matches in the other tab" — Users/Tenants can't.
    const otherKindAggregated = useMemo<UserRow[]>(() => {
        if (state.kind === 'users' || state.kind === 'tenants') {
            return [];
        }
        // Three capabilities means there is no single "other" tab: aggregate every
        // kind except the active one, deduplicating users by email.
        const others = KIND_ORDER.filter(k => k !== state.kind);
        const byEmail = new Map<string, UserRow>();
        for (const other of others) {
            for (const row of aggregateUserAccess(grants, other)) {
                const existing = byEmail.get(row.email);
                if (existing) {
                    existing.chips.push(...row.chips);
                } else {
                    byEmail.set(row.email, {...row, chips: [...row.chips]});
                }
            }
        }
        return [...byEmail.values()];
    }, [grants, state.kind]);

    const tenantFilter = useMemo(() => new Set(state.tenants), [state.tenants]);

    const filtered = useMemo(() => {
        const q = state.search.trim().toLowerCase();
        const expiryCap = state.expiry === '7d' ? 7 : state.expiry === '30d' ? 30 : Number.POSITIVE_INFINITY;
        const out: UserRow[] = [];
        for (const row of aggregated) {
            if (q) {
                const rowMatch =
                    row.email.toLowerCase().includes(q) ||
                    row.name.toLowerCase().includes(q) ||
                    row.chips.some(c => c.resource.toLowerCase().includes(q)) ||
                    row.chips.some(c => c.resourceName?.toLowerCase().includes(q)) ||
                    row.chips.some(c =>
                        c.grants.some(g => {
                            const raw = g.raw;
                            if (!raw) {
                                return false;
                            }
                            for (const v of Object.values(raw)) {
                                if (typeof v === 'string' && v.toLowerCase().includes(q)) {
                                    return true;
                                }
                            }
                            return false;
                        })
                    );
                if (!rowMatch) {
                    continue;
                }
            }
            const chips = row.chips.filter(c => {
                // Filter values are canonical tenant ids (name when known) — they
                // collapse the shard-uid and account-id form of the same tenant.
                if (tenantFilter.size > 0 && !tenantFilter.has(c.resourceName ?? c.resource)) {
                    return false;
                }
                if (state.permanent && !c.permanent) {
                    return false;
                }
                if (state.expiry !== 'none') {
                    if (c.permanent) {
                        return false;
                    }
                    if (c.daysRemaining < 0 || c.daysRemaining > expiryCap) {
                        return false;
                    }
                }
                return true;
            });
            if (chips.length === 0) {
                continue;
            }
            out.push({...row, chips});
        }
        return out;
    }, [aggregated, state.search, state.permanent, state.expiry, tenantFilter]);

    const filteredTenants = useMemo<TenantRow[]>(() => {
        if (state.kind !== 'tenants') {
            return [];
        }
        const q = state.search.trim().toLowerCase();
        const expiryCap = state.expiry === '7d' ? 7 : state.expiry === '30d' ? 30 : Number.POSITIVE_INFINITY;
        const matchUser = (u: {email: string; name: string; grants: GrantView[]}, query: string) =>
            u.email.toLowerCase().includes(query) ||
            u.name.toLowerCase().includes(query) ||
            u.grants.some(g => {
                const raw = g.raw;
                if (!raw) {
                    return false;
                }
                for (const v of Object.values(raw)) {
                    if (typeof v === 'string' && v.toLowerCase().includes(query)) {
                        return true;
                    }
                }
                return false;
            });
        const passesUserFilters = (u: {permanent: boolean; daysRemaining: number}) => {
            if (state.permanent && !u.permanent) {
                return false;
            }
            if (state.expiry !== 'none') {
                if (u.permanent) {
                    return false;
                }
                if (u.daysRemaining < 0 || u.daysRemaining > expiryCap) {
                    return false;
                }
            }
            return true;
        };
        const out: TenantRow[] = [];
        for (const row of tenantsAggregated) {
            const canon = row.resourceName ?? row.resource;
            if (tenantFilter.size > 0 && !tenantFilter.has(canon)) {
                continue;
            }
            const tenantMatchesQuery =
                !q ||
                row.resourceName?.toLowerCase().includes(q) ||
                row.resourceIds.some(id => id.toLowerCase().includes(q));
            // Filter each capability's bucket independently, then drop the tenant
            // row only when EVERY bucket came back empty.
            const usersByKind = emptyUsersByKind();
            const seen = new Set<string>();
            let kept = 0;
            for (const k of KIND_ORDER) {
                const users = row.usersByKind[k]
                    .filter(u => (q && !tenantMatchesQuery ? matchUser(u, q) : true))
                    .filter(passesUserFilters);
                usersByKind[k] = users;
                kept += users.length;
                for (const u of users) {
                    seen.add(u.email);
                }
            }
            if (kept === 0) {
                continue;
            }
            out.push({
                ...row,
                usersByKind,
                totalUsers: seen.size,
            });
        }
        return out;
    }, [tenantsAggregated, state.kind, state.search, state.permanent, state.expiry, tenantFilter]);

    const totalUsers = filtered.length;
    const totalGrants = useMemo(
        () => filtered.reduce((sum, r) => sum + r.chips.reduce((s, c) => s + c.grants.length, 0), 0),
        [filtered]
    );
    const totalTenants = filteredTenants.length;
    const totalTenantGrants = useMemo(
        () =>
            filteredTenants.reduce(
                (sum, t) =>
                    sum +
                    KIND_ORDER.reduce(
                        (kSum, k) => kSum + t.usersByKind[k].reduce((s, u) => s + u.grants.length, 0),
                        0
                    ),
                0
            ),
        [filteredTenants]
    );

    /** Did the search query match this row (regardless of chip filters)? */
    const rowMatchesSearch = useCallback(
        (row: UserRow, q: string): boolean => {
            if (!q) {
                return true;
            }
            return (
                row.email.toLowerCase().includes(q) ||
                row.name.toLowerCase().includes(q) ||
                row.chips.some(c => c.resource.toLowerCase().includes(q)) ||
                row.chips.some(c => c.resourceName?.toLowerCase().includes(q)) ||
                row.chips.some(c =>
                    c.grants.some(g => {
                        const raw = g.raw;
                        if (!raw) {
                            return false;
                        }
                        for (const v of Object.values(raw)) {
                            if (typeof v === 'string' && v.toLowerCase().includes(q)) {
                                return true;
                            }
                        }
                        return false;
                    })
                )
            );
        },
        []
    );

    const q = state.search.trim().toLowerCase();
    const hasChipFilters = state.permanent || state.expiry !== 'none' || tenantFilter.size > 0;
    const searchMatchesInTab = useMemo(
        () => (q ? aggregated.filter(r => rowMatchesSearch(r, q)).length : 0),
        [aggregated, q, rowMatchesSearch]
    );
    const searchMatchesInOtherTab = useMemo(
        () => (q ? otherKindAggregated.filter(r => rowMatchesSearch(r, q)).length : 0),
        [otherKindAggregated, q, rowMatchesSearch]
    );
    /** Label for the "N matches in other tabs" hint. */
    const otherTabName = 'other tabs';

    const clearFilters = () => update({permanent: false, expiry: 'none', tenants: []});

    // Pagination targets either the user list or the tenant list depending on the tab.
    const totalForPaging = state.kind === 'tenants' ? totalTenants : totalUsers;
    const pageCount = Math.max(1, Math.ceil(totalForPaging / state.pageSize));
    const safePage = Math.max(0, Math.min(state.page, pageCount - 1));
    const pageStart = safePage * state.pageSize;
    const pageRows = filtered.slice(pageStart, pageStart + state.pageSize);
    const pageTenants = filteredTenants.slice(pageStart, pageStart + state.pageSize);

    if (import.meta.env.DEV && state.search) {
        // eslint-disable-next-line no-console
        console.debug('[accessops] search=%o → users=%d, grants=%d, first row=%o', state.search, totalUsers, totalGrants, pageRows[0]);
    }

    const openDrawer = (email: string) => setDrawerEmail(email);
    const closeDrawer = () => setDrawerEmail(null);
    // Tenant drawer is anchored on the chip itself — we keep the kind so the
    // drawer can header it as "Data access" or "Impersonation" (or "All" when
    // clicked from the wildcard or All-access view).
    const openTenantDrawer = (chip: TenantChip) => {
        setTenantDrawer({resource: chip.resource, kind: chip.kind});
    };
    const closeTenantDrawer = () => setTenantDrawer(null);

    const drawerGrants: GrantView[] = useMemo(() => {
        if (!drawerEmail) {
            return [];
        }
        return grants.filter(g => {
            if (g.email !== drawerEmail) {
                return false;
            }
            if (state.kind === 'users') {
                return tabKindOf(g.kind) !== null;
            }
            return tabKindOf(g.kind) === state.kind;
        });
    }, [drawerEmail, grants, state.kind]);

    const drawerName = useMemo(() => {
        if (!drawerEmail) {
            return null;
        }
        return aggregated.find(r => r.email === drawerEmail)?.name ?? null;
    }, [drawerEmail, aggregated]);

    const stub = (msg: string) => {
        console.info('[accessops-stub]', msg);
        setToast('Phase 2 backend not deployed yet');
    };

    const onCellClick = (_email: string, chip: TenantChip) => {
        openTenantDrawer(chip);
    };

    const variantForChips: ChipVariant = state.variant === 'matrix' ? 'chips' : state.variant;

    if (twingateDown) {
        return (
            <main className="page">
                <div className="twingate-prompt">
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden
                        style={{marginBottom: 16}}
                    >
                        <path
                            d="M12 2L3 7v6c0 5 4 8 9 9 5-1 9-4 9-9V7l-9-5z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                        />
                        <path
                            d="M9 12l2 2 4-4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h2>Подключите Twingate</h2>
                    <p>
                        API доступен только через Twingate. Открой клиент Twingate и подключись,
                        затем нажми Reload.
                    </p>
                    <button type="button" className="btn btn-primary" onClick={onReload} disabled={loading}>
                        {loading ? 'Checking…' : 'Reload'}
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="page">
                <div className="page-head">
                    <h1>Grants</h1>
                    <div className="spacer" />
                    {data?.syncedAt && (
                        <span className="synced-ago" title={new Date(data.syncedAt).toLocaleString()}>
                            synced {formatAgo(data.syncedAt)}
                        </span>
                    )}
                    <button
                        type="button"
                        className="btn-reload"
                        onClick={onReload}
                        disabled={loading}
                    >
                        {loading ? 'Syncing…' : 'Reload'}
                    </button>
                </div>
                <div className="page-subhead">
                    Who has access to what, across IAM and AccessOps. Click a row or chip for details.
                </div>

                {error && (
                    <Alert severity="error" sx={{mb: 2}}>
                        {error}
                    </Alert>
                )}
                {data?.errors.map(e => (
                    <Alert key={e.source} severity="warning" sx={{mb: 2}}>
                        <strong>{e.source}</strong>: {e.message}
                    </Alert>
                ))}

                <div className="tabs" role="tablist">
                    {/* Per-capability views FIRST, in the canonical order (Ennabl AI,
                        app-neutral Data Access, Decisions) — these are what an
                        operator grants and audits day to day, and Ennabl AI is the
                        default landing tab. Users and Tenants follow: they are
                        cross-capability aggregations, useful but secondary.
                        Only the dot colour distinguishes the two impersonation
                        tabs — the tab's own text colour must NOT vary per app, or
                        it reads as three different active states. */}
                    {KIND_ORDER.map(k => (
                        <button
                            key={k}
                            type="button"
                            role="tab"
                            aria-selected={state.kind === k}
                            className={`tab tab-two-line${state.kind === k ? ' active' : ''}`}
                            onClick={() => update({kind: k})}
                        >
                            <span className="tab-app">
                                <span className={`app-dot ${chipKindClass(k)}`} />
                                {KIND_LABEL[k].app}
                            </span>
                            <span className="tab-type">
                                {KIND_LABEL[k].type}
                                {/* Unfiltered on purpose: this count sits in the tab
                                    header and must not shrink as the user types. */}
                                <span className="count">{tabCounts.perKind[k]}</span>
                            </span>
                        </button>
                    ))}
                    <button
                        type="button"
                        role="tab"
                        aria-selected={state.kind === 'users'}
                        className={`tab${state.kind === 'users' ? ' active' : ''}`}
                        onClick={() => update({kind: 'users'})}
                    >
                        Users <span className="count">{tabCounts.users}</span>
                    </button>
                </div>

                {loading && !data && (
                    <div className="card">
                        <Spinner label="Loading grants…" sublabel="IAM · AccessOps · shard config" />
                    </div>
                )}

                {data && (
                    <>
                <div className="toolbar">
                    <div className="search">
                        <svg viewBox="0 0 24 24" fill="none">
                            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search email, tenant, or username…"
                            value={searchDraft}
                            onChange={e => setSearchDraft(e.target.value)}
                        />
                    </div>

                    <MultiSelectFilter
                        label={resourceAxisLabel(
                            state.kind === 'users' || state.kind === 'tenants' ? 'mixed' : state.kind
                        )}
                        options={allTenantsForKind}
                        selected={tenantFilter}
                        onChange={next => update({tenants: Array.from(next)})}
                    />

                    <button
                        type="button"
                        className={`chip-toggle${state.permanent ? ' on' : ''}`}
                        onClick={() => update({permanent: !state.permanent})}
                    >
                        Permanent only
                    </button>
                    <button
                        type="button"
                        className={`chip-toggle${state.expiry === '7d' ? ' on' : ''}`}
                        onClick={() => update({expiry: state.expiry === '7d' ? 'none' : '7d'})}
                    >
                        Expiring ≤ 7d
                    </button>
                    <button
                        type="button"
                        className={`chip-toggle${state.expiry === '30d' ? ' on' : ''}`}
                        onClick={() => update({expiry: state.expiry === '30d' ? 'none' : '30d'})}
                    >
                        Expiring ≤ 30d
                    </button>

                    <div className="toolbar-spacer" />

                    {/* View selector hidden — not deleted; bring back when chip
                       variants / matrix view earn their keep. */}
                    {false && (
                        <div className="select" style={{cursor: 'default'}}>
                            <span className="label">View:</span>
                            <select
                                value={state.variant}
                                onChange={e => update({variant: e.target.value as UrlState['variant']})}
                                style={{
                                    border: 0,
                                    background: 'transparent',
                                    font: 'inherit',
                                    color: 'inherit',
                                    cursor: 'pointer',
                                    outline: 0,
                                }}
                            >
                                <option value="chips">Chips</option>
                                <option value="compact">Compact</option>
                                <option value="minimal">Minimal</option>
                                <option value="matrix">Matrix</option>
                            </select>
                        </div>
                    )}

                    <div className="toolbar-summary">
                        {state.kind === 'tenants' ? (
                            <>
                                {totalTenants} tenants · {totalTenantGrants} grants
                            </>
                        ) : (
                            <>
                                {totalUsers} users · {totalGrants} grants
                            </>
                        )}
                    </div>
                </div>

                {state.kind === 'tenants' ? (
                    <div className="card">
                        {pageTenants.length === 0 ? (
                            <div className="empty">
                                <h3>No matching tenants</h3>
                                <div>
                                    {state.search
                                        ? `Nothing matches "${state.search}" by tenant name, id, or user.`
                                        : 'No tenant grants to show.'}
                                </div>
                            </div>
                        ) : (
                            <table className="tbl tbl-balanced">
                                <thead>
                                    <tr>
                                        <th className="col-user">Tenant</th>
                                        {KIND_ORDER.map(k => (
                                            <th key={k}>{kindHeading(k)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageTenants.map(t => (
                                        <tr
                                            key={t.resourceName ?? t.resource}
                                            onClick={() =>
                                                setTenantDrawer({resource: t.resource, kind: 'both'})
                                            }
                                        >
                                            <td className="col-user">
                                                <div className="user-cell">
                                                    <span
                                                        className={`avatar${t.resource === '*' ? ' avatar-wildcard' : ''}`}
                                                        aria-hidden
                                                    >
                                                        {t.resource === '*'
                                                            ? '∗'
                                                            : t.resource === '?'
                                                            ? '?'
                                                            : (t.resourceName ?? t.resource)
                                                                  .slice(0, 2)
                                                                  .toUpperCase()}
                                                    </span>
                                                    <div className="meta">
                                                        <div className="name">
                                                            {t.resource === '*'
                                                                ? 'ALL'
                                                                : t.resourceName ?? t.resource}
                                                        </div>
                                                        <div className="email">
                                                            {t.totalUsers} {t.totalUsers === 1 ? 'user' : 'users'}
                                                            {t.resourceName ? ` · ${t.resourceIds.join(', ')}` : ''}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            {KIND_ORDER.map(k => (
                                                <td key={k}>
                                                    <div className="access-chips">
                                                        {t.usersByKind[k].map(u => (
                                                            <UserChip key={`${k}-${u.email}`} chip={u} />
                                                        ))}
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {pageTenants.length > 0 && (
                            <div className="pagination">
                                <div className="rpp">
                                    <span>Rows per page</span>
                                    <select
                                        value={state.pageSize}
                                        onChange={e => update({pageSize: Number(e.target.value)})}
                                    >
                                        {[10, 25, 50, 100].map(n => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <span className="range">
                                    {pageStart + 1}–
                                    {Math.min(pageStart + state.pageSize, totalTenants)} of {totalTenants}
                                </span>
                                <div className="page-btns">
                                    <button
                                        type="button"
                                        className="pg"
                                        disabled={safePage === 0}
                                        onClick={() => update({page: safePage - 1})}
                                        aria-label="Previous page"
                                    >
                                        ‹
                                    </button>
                                    <button
                                        type="button"
                                        className="pg"
                                        disabled={safePage >= pageCount - 1}
                                        onClick={() => update({page: safePage + 1})}
                                        aria-label="Next page"
                                    >
                                        ›
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : state.variant === 'matrix' ? (
                    <MatrixView
                        rows={filtered}
                        otherKindRows={otherKindAggregated}
                        activeKind={state.kind}
                        onCellClick={onCellClick}
                    />
                ) : (
                    <div className="card">
                        {pageRows.length === 0 ? (
                            <div className="empty">
                                <h3>No matching grants</h3>
                                {state.search && searchMatchesInTab > 0 && hasChipFilters ? (
                                    <>
                                        <div>
                                            {searchMatchesInTab} {searchMatchesInTab === 1 ? 'user matches' : 'users match'}{' '}
                                            “{state.search}” but their chips are hidden by active filters.
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-text"
                                            style={{marginTop: 12}}
                                            onClick={clearFilters}
                                        >
                                            Clear filters
                                        </button>
                                    </>
                                ) : state.search && searchMatchesInOtherTab > 0 ? (
                                    <>
                                        <div>
                                            {searchMatchesInOtherTab}{' '}
                                            {searchMatchesInOtherTab === 1 ? 'match' : 'matches'} for “{state.search}” in{' '}
                                            <strong>{otherTabName}</strong>.
                                        </div>
                                        {/* With three capabilities there is no single
                                            "other" tab to jump to, so send them to Users,
                                            which shows every capability at once. */}
                                        <button
                                            type="button"
                                            className="btn btn-text"
                                            style={{marginTop: 12}}
                                            onClick={() => update({kind: 'users'})}
                                        >
                                            Show all capabilities
                                        </button>
                                    </>
                                ) : state.search ? (
                                    <div>
                                        Nothing matches “{state.search}” in email, tenant, or username. Names aren&apos;t
                                        stored — try the email prefix.
                                    </div>
                                ) : hasChipFilters ? (
                                    <>
                                        <div>No grants match the active filters.</div>
                                        <button
                                            type="button"
                                            className="btn btn-text"
                                            style={{marginTop: 12}}
                                            onClick={clearFilters}
                                        >
                                            Clear filters
                                        </button>
                                    </>
                                ) : (
                                    <div>Try selecting another tab.</div>
                                )}
                            </div>
                        ) : (
                            <table className={`tbl${state.kind === 'users' ? ' tbl-balanced' : ''}`}>
                                <thead>
                                    <tr>
                                        <th className="col-user">User</th>
                                        {state.kind === 'users' ? (
                                            <>
                                                {KIND_ORDER.map(k => (
                                                    <th key={k}>{kindHeading(k)}</th>
                                                ))}
                                            </>
                                        ) : (
                                            <th>{resourceColumnHeading(state.kind)}</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.map(row => (
                                        <tr key={row.email} onClick={() => openDrawer(row.email)}>
                                            <td className="col-user">
                                                <div className="user-cell">
                                                    <span className="avatar" aria-hidden>
                                                        {row.name
                                                            ? row.name
                                                                  .split(/\s+/)
                                                                  .slice(0, 2)
                                                                  .map(p => p[0])
                                                                  .join('')
                                                                  .toUpperCase()
                                                            : row.email.slice(0, 2).toUpperCase()}
                                                    </span>
                                                    <div className="meta">
                                                        <div className="name">{row.name || row.email}</div>
                                                        <div className="email">{row.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            {state.kind === 'users' ? (
                                                <>
                                                    {KIND_ORDER.map(k => (
                                                        <td key={k}>
                                                            <div className="access-chips">
                                                                {row.chips
                                                                    .filter(c => c.kind === k)
                                                                    .map(c => (
                                                                        <AccessChip
                                                                            key={`${c.resource}-${c.kind}`}
                                                                            chip={c}
                                                                            variant={variantForChips}
                                                                        />
                                                                    ))}
                                                            </div>
                                                        </td>
                                                    ))}
                                                </>
                                            ) : (
                                                <td>
                                                    <div className="access-chips">
                                                        {row.chips.map(c => (
                                                            <AccessChip
                                                                key={`${c.resource}-${c.kind}`}
                                                                chip={c}
                                                                variant={variantForChips}
                                                            />
                                                        ))}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {pageRows.length > 0 && (
                            <div className="pagination">
                                <div className="rpp">
                                    <span>Rows per page</span>
                                    <select
                                        value={state.pageSize}
                                        onChange={e => update({pageSize: Number(e.target.value)})}
                                    >
                                        {[10, 25, 50, 100].map(n => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <span className="range">
                                    {pageStart + 1}–{Math.min(pageStart + state.pageSize, totalUsers)} of {totalUsers}
                                </span>
                                <div className="page-btns">
                                    <button
                                        type="button"
                                        className="pg"
                                        disabled={safePage === 0}
                                        onClick={() => update({page: safePage - 1})}
                                        aria-label="Previous page"
                                    >
                                        ‹
                                    </button>
                                    <button
                                        type="button"
                                        className="pg"
                                        disabled={safePage >= pageCount - 1}
                                        onClick={() => update({page: safePage + 1})}
                                        aria-label="Next page"
                                    >
                                        ›
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                    </>
                )}

            <UserDrawer
                open={drawerEmail !== null}
                email={drawerEmail}
                name={drawerName}
                kind={state.kind}
                grants={drawerGrants}
                onClose={closeDrawer}
                onRevoke={async g => {
                    if (!isRevocable(g)) {
                        setToast(
                            'Revoke supported for: Impersonation rows, DataAccess all-tenants rows, and IAM TemporaryDataAccess policies. Per-tenant DataAccess rows are managed by check-user-access.'
                        );
                        return;
                    }
                    const target =
                        g.resource === '*'
                            ? 'all tenants'
                            : g.resourceName ?? g.resource;
                    if (!window.confirm(`Revoke ${g.kind} for ${g.email} → ${target}?`)) {
                        return;
                    }
                    try {
                        const r = await revokeGrant(session.idToken, g.id);
                        if (r.ok) {
                            setToast(`Revoked (${r.kind}). Reloading grants…`);
                            onReload();
                            closeDrawer();
                        } else {
                            setToast(`Revoke failed: ${r.error ?? 'unknown'}`);
                        }
                    } catch (err) {
                        setToast(
                            `Revoke failed: ${err instanceof Error ? err.message : String(err)}`
                        );
                    }
                }}
                onAddGrant={() => stub(`add grant for ${drawerEmail}`)}
                onExtendAll={() => stub(`extend all for ${drawerEmail}`)}
                onRevokeAll={() => stub(`revoke all for ${drawerEmail}`)}
            />

            <TenantDrawer
                open={tenantDrawer !== null}
                resource={tenantDrawer?.resource ?? null}
                kind={tenantDrawer?.kind ?? null}
                allGrants={grants}
                onClose={closeTenantDrawer}
            />

            <Snackbar
                open={toast !== null}
                autoHideDuration={3000}
                onClose={() => setToast(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert severity="info" onClose={() => setToast(null)} variant="filled">
                    {toast}
                </Alert>
            </Snackbar>
        </main>
    );
}
